import { useEffect } from 'react';
import { useLocation } from 'react-router';

/**
 * Lightweight hook to set document title and meta description per page.
 * No external dependency needed — just sets DOM directly.
 *
 * Also keeps `og:url` in step with the actual route. index.html ships a
 * static `og:url` (the site root) because this is a client-rendered SPA with
 * no SSR/prerendering — every route resolves to the same static file, so a
 * crawler that never executes JS (most social-share bots: Slack, WhatsApp,
 * iMessage, and Twitterbot/facebookexternalhit in their default mode) has no
 * way to see a per-route value regardless of what we do here, and the root
 * URL is the correct, honest fallback for those. For the crawlers and
 * consumers that DO run JS (Googlebot, LinkedIn's on-demand fetcher), this
 * updates `og:url` to the current location, so it never claims a route other
 * than the one actually being viewed/shared.
 *
 * The og:url sync runs as its OWN effect keyed on the route (Codex review):
 * a page that calls usePageMeta with a constant title/description (e.g.
 * BookingFlow's `usePageMeta('Book a Service | Kanfy')`) but changes its
 * query string via setSearchParams -- for the wizard's step/recovery params
 * -- never re-ran the title/description effect, so og:url stayed pinned to
 * whatever URL was current on mount instead of following the step.
 */
export function usePageMeta(title: string, description?: string) {
  const location = useLocation();

  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    const metaDesc = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    let prevDescription = '';
    if (description && metaDesc) {
      prevDescription = metaDesc.content;
      metaDesc.content = description;
    }

    return () => {
      document.title = prevTitle;
      if (description && metaDesc) {
        metaDesc.content = prevDescription;
      }
    };
  }, [title, description]);

  useEffect(() => {
    const metaOgUrl = document.querySelector<HTMLMetaElement>('meta[property="og:url"]');
    let prevOgUrl = '';
    if (metaOgUrl) {
      prevOgUrl = metaOgUrl.content;
      // Built from the router's own location, not window.location.href directly --
      // origin is stable regardless of router type, and this ties the value to
      // exactly the state this effect's deps track (pathname/search/hash).
      metaOgUrl.content = window.location.origin + location.pathname + location.search + location.hash;
    }

    return () => {
      if (metaOgUrl) {
        metaOgUrl.content = prevOgUrl;
      }
    };
  }, [location.pathname, location.search, location.hash]);
}
